var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, makeObservable } from 'mobx';
import { default as seededshuffle } from 'seededshuffle';
import { QuestionTypeName } from '../../classes/learnware/LearnwareEnums';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { Choice } from '../Choice/Choice';
import { alias, disgardOldValue, object } from '../../utils/serializr/serializr';
import { AssociateScore } from './modules/AssociateScore';
import { AssociateStore } from './modules/AssociateStore';
import { AssociateItems } from './modules/AssociateItems';
import { Question } from '../../classes/question/Question';
import { primitive } from '../../utils/serializr/schema/inner/primitive';
import { AssociateAnsweringFeedback } from './modules/AssociateAnsweringFeedback';
import { withInit } from '../../utils/serializr/schema/middle/withInit';
import { strip } from '../../utils/serializr/schema/inner/strip';
import { runtime } from '../../classes/runtime/Runtime';
import { head } from '../../utils/helper/lodash_replacements';
import { editable } from '../../utils/mobx/decorators/editable';
export class QuestionAssociate extends Choice {
    questionType = QuestionTypeName.Associate;
    manuallySelectedAssociation;
    associateItems = new AssociateItems(this);
    constructor(init) {
        super(init);
        makeObservable(this);
    }
    get exportingChildren() {
        return [...super.exportingChildren, ...this.allAssociations];
    }
    get associateStore() {
        return this.choiceStore;
    }
    get questionScore() {
        return new AssociateScore(this);
    }
    get answeringFeedback() {
        return new AssociateAnsweringFeedback(this);
    }
    get associateEdit() {
        return new runtime.solidPackages.Edit.classes.AssociateEdit(this);
    }
    get allAssociations() {
        return this.associateStore.allAssociations;
    }
    get shuffledAssociations() {
        if (this.shouldShuffle) {
            return seededshuffle.shuffle(this.allAssociations, this.shuffleSeed + this.shuffleSeed, true); // seed must be different than option shuffle seed
        }
        return this.allAssociations;
    }
    get associationsToAsk() {
        return this.shuffledAssociations.slice(0, this.associateStore.askNum);
    }
    get numberOfCurrentAssociation() {
        if (!this.currentAssociation)
            return 0;
        return this.associationsToAsk.indexOf(this.currentAssociation) + 1;
    }
    get remainingAssociations() {
        return this.associationsToAsk.filter(a => !a.isAssociationFinished);
    }
    get numberOfTries() {
        return this.associateStore.numberOfTries;
    }
    get currentAssociation() {
        if (this.context.app.appInfo.isUserCurrentlyEditing) {
            return this.associateEdit.currentEditAssociation;
        }
        if (this.manuallySelectedAssociation && this.remainingAssociations.includes(this.manuallySelectedAssociation)) {
            return this.manuallySelectedAssociation;
        }
        return head(this.remainingAssociations);
    }
    get options() {
        return super.options;
    }
    static get serializeInfo() {
        return {
            extends: Question.serializeInfo,
            props: {
                questionType: withInit(primitive, QuestionTypeName.Associate),
                choiceStore: /*earlyReference(AssociateStore,*/ alias('questionTypeStore', withInit(object(AssociateStore), {})) /*)*/,
                textBasis: alias('text', disgardOldValue(primitive)),
            },
        };
    }
    static get stripInfo() {
        return {
            extends: Question.stripInfo,
            props: {
                askNum: strip((q) => q.associateStore.askNum),
                associations: strip((q) => q.associateStore.allAssociations.map(a => a.stripped)),
                options: strip((q) => q.associateStore.allOptions.map(a => a.stripped)),
            },
        };
    }
}
__decorate([
    editable()
], QuestionAssociate.prototype, "questionType", void 0);
__decorate([
    observable.ref
], QuestionAssociate.prototype, "manuallySelectedAssociation", void 0);
__decorate([
    memoize
], QuestionAssociate.prototype, "questionScore", null);
__decorate([
    memoize
], QuestionAssociate.prototype, "answeringFeedback", null);
__decorate([
    memoize
], QuestionAssociate.prototype, "associateEdit", null);
__decorate([
    computed
], QuestionAssociate.prototype, "currentAssociation", null);
__decorate([
    memoize
], QuestionAssociate, "serializeInfo", null);
__decorate([
    memoize
], QuestionAssociate, "stripInfo", null);
